import { Shot } from '../../page/landing-layout/tool-pages/product-shots';

/**
 * One scene of a feature: a picture of its own, its name (in the lightbox), a label on it or a
 * caption under it, and the page file's size, so the picture keeps its room before it loads.
 */
export interface ProductScene {
  key: string;
  name: string;
  /** A small pill on the picture (the Dock Town grids). */
  label?: string;
  /** One line under the picture on the product page (the mini games). */
  caption?: string;
  alt: string;
  width: number;
  height: number;
}

export type ProductLayout = 'one' | 'four' | 'twelve' | 'phones' | 'trio' | 'grid';

/**
 * One thing a product does: its headline (the accent words first), its one line (the home's
 * showcase), its full description (the product page's section), and its scenes.
 *  one     a single wide scene, whole
 *  four    a 2 x 2 grid of wide scenes, cropped to their cells
 *  twelve  a 4 x 3 grid of wide scenes
 *  phones  one to three phone screens side by side, at one height
 *  trio    three phone screens, the middle one bigger and in front (the App Store's hero look)
 *  grid    a grid of phone screens with captions (the product page only)
 */
export interface ProductFeature {
  key: string;
  chip: string;
  accent: string;
  rest: string;
  line: string;
  description: string;
  layout: ProductLayout;
  scenes: ProductScene[];
  /** The home's showcase shows these instead of `scenes` when set (LangMi: the screens in their bezels, one picture). */
  homeScenes?: ProductScene[];
  homeLayout?: ProductLayout;
  /** False for the features only the product page shows (the home's showcase keeps to the App Store's set). */
  home?: boolean;
  /** False for the slides only the home's showcase shows (the product page has its own section for the same thing). */
  page?: boolean;
  /** The product page section's id, when something links to it (/langmi#mini-games). */
  anchor?: string;
  /**
   * The showcase's left side for this slide, when the slides are one product on several
   * platforms (the home's Color Pickers card, owner 2026-10-10): each falls back to the config's.
   */
  eyebrow?: string;
  eyebrowLogo?: string;
  /** a slide's own name, lead and text on the left (the Our Services box: one service a slide); the
   *  config's otherwise */
  title?: string;
  lead?: string;
  text?: string;
  icon?: string;
  facts?: string[];
  link?: string;
  soon?: string;
  /** The store button in place of the note, for a product that is out (the pages' own install button). */
  store?: ProductStore;
  /** A product sold on the site (2026-10-14: the macOS Color Picker): its store sku. While the store sells it, the
   *  slide shows Add to cart with the price beside it, as the themes box does, in place of the store button. */
  cartSku?: string;
  /** A logo in the slide's chip (the platform's). */
  chipLogo?: string;
}

/** A product's store: the button's words, its address, and the install event the product's page logs. */
export interface ProductStore {
  label: string;
  /** a Mac App Store listing: on a Mac the click opens the App Store app (util/mac-app-store.ts) */
  macApp?: boolean;
  url: string;
  event: string;
  attributes: Record<string, string>;
}

/** Where a product's scene files live: `${ dir }${ prefix }${ key }.webp` and `-large.webp`. */
export interface ProductPictures {
  dir: string;
  prefix: string;
}

/** What the home's showcase says and shows for one product (product-showcase.component.ts). */
export interface ShowcaseConfig {
  key: string;
  /** Wide scenes (Dock Town) or phone screens (LangMi): the stage lays them out differently. */
  shape: 'scene' | 'phone';
  eyebrowLogo: string;
  eyebrow: string;
  icon: string;
  title: string;
  lead: string;
  text: string;
  facts: string[];
  link: string;
  cta: string;
  soon: string;
  pickerLabel: string;
  pictures: ProductPictures;
  slides: ProductFeature[];
}

export function sceneSrc( pictures: ProductPictures, scene: ProductScene ): string {
  return `${ pictures.dir }${ pictures.prefix }${ scene.key }.webp`;
}

export function sceneLarge( pictures: ProductPictures, scene: ProductScene ): string {
  return `${ pictures.dir }${ pictures.prefix }${ scene.key }-large.webp`;
}

/** The lightbox entry of a scene: the page's file, the full-size one, the name and the alt text. */
export function sceneShot( pictures: ProductPictures, scene: ProductScene ): Shot {
  return { name: scene.name, src: sceneSrc( pictures, scene ), large: sceneLarge( pictures, scene ), alt: scene.alt };
}

/** Every scene of every feature as a lightbox entry, in order, so the arrows walk through them all. */
export function featureShots( pictures: ProductPictures, features: ProductFeature[] ): Shot[] {
  return features.reduce<Shot[]>( ( all, f ) => all.concat( f.scenes.map( s => sceneShot( pictures, s ) ) ), [] );
}
